import { Injectable } from '@angular/core';
import { NgRedux, select } from '@ptl/angular-redux-store';
import { savePlanIdForEnrollment, savePlanNameForEnrollment } from 'src/app/angular-elements/plan-selection/configs/reducers/plans.reducer';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Product } from 'src/app/angular-elements/plan-selection/interfaces/product.interface';
import { OnExchangePlanAttributes, PlanAttributes, SummaryView } from 'src/app/angular-elements/plan-selection/interfaces/plan-attributes.interface';
import { Observable, Subscription } from 'rxjs';
import { PtlWindowLocationService } from '@ptl/global-components';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { OnExchangeEligibilityStateStructure, ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME } from 'src/app/angular-elements/on-exchange-enrollment/reducers/eligibility.reducer';
import { EligibilityManagerService } from 'src/app/angular-elements/on-exchange-enrollment/services/eligibility-manager.service';
import { NoEnrollCatastrophicModalComponent } from '../../../components/enroll-button/on-exchange-modal-popups/no-enroll-catastrophic-modal/no-enroll-catastrophic-modal.component';
import { WarnEnrollNonSilverComponent } from '../../../components/enroll-button/on-exchange-modal-popups/warn-enroll-non-silver/warn-enroll-non-silver.component';
import { PlanDetailsService } from 'src/app/angular-elements/plan-selection/plan-details/plan-details.service';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';

  /** ONX Enrollment Rules */
  /**
   * 1) CATASTROPHIC RULES:  you cannot enroll via ONX under any circumstance via the portal
   * 2) CSR:  if you have a cost-saving reduction, you can only have it applied on METAL_LEVEL === SILVER plans
   *  ( we warn the user if they attempt to enroll in METAL_LEVEL !== SILVER plans )
   * "Please note: You are only eligible for cost-sharing reductions (CSR) if you choose a Silver-level plan.  If you would like to select a
   *    Silver-level plan, close this window and make a new selection."
   * 3) APTC: if you have an advance-premium-tax-credit option, and you've selected $0 applied
   *  ( we warn the user they have hthe opportunity to applie one )
   * "Please note: you are eligible to apply an advance premium tax credit (APTC) to your monthly premium. If you would like to apply your APTC to
   *  your premium, please close this window and make a new selection.""
   * 4) If both CSR and APTC are available and not currently being taken advantage of (METAL_LEVEL !=== SILVER || selectedAPTC === 0)
   *  ( we warn the user with both scenario 3 and 4 warnings)
   *
   */
  @Injectable({
    providedIn: 'root'
})
  export class OnExchangeEnrollmentCheckService {
  
    @select(['appStates', 'ShoppingDates']) shoppingDates$: Observable<any>;
    shoppingDatesSubscription: Subscription;
    @select(['appStates', 'EffectiveDate']) effectiveDate$: Observable<any>;
    effectiveDateSubscription: Subscription;
  
    processingONX: boolean;
  
    enrollButtonText = 'ENROLL';
  
    constructor(
      private ngRedux: NgRedux<any>,
      private matDialog: MatDialog,
      private windowLocationService: PtlWindowLocationService,
      private reducerUtilityService: ReducerUtilityService,
      private eligibilityManagerService: EligibilityManagerService,
      private planDetailsService: PlanDetailsService,
      private snackBar: MatSnackBar
    ) { }

async doOnXEnrollChecks(enrollmentStartUrl: string, currentPlan: Product<PlanAttributes, SummaryView>) {
    // TODO: isolate this and move it to its own service please.
    const proceedToEnroll = async () => {
      this.eligibilityManagerService.updateIndividualResponsibilityAmount(
        currentPlan.price as string,
        (currentPlan.attributes as OnExchangePlanAttributes).EHB
      );

      const eligibilityState: OnExchangeEligibilityStateStructure
        = this.reducerUtilityService.getComponentStates()[ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME];
      const matchedProspects = eligibilityState.prospects.filter(p => eligibilityState.currentProspectId === p.prospectId);

      if (matchedProspects.length > 0) {

        const prospect = matchedProspects[0];

        this.processingONX = true;
        const planDetailsResponse
          = await this.planDetailsService.getPlansByMatchedIds(
            [currentPlan.productId],
            'Individual'
          );

        if (!(planDetailsResponse instanceof HttpErrorResponse)) {
          prospect.data.selectedPlan = planDetailsResponse.rawData[0];
          this.eligibilityManagerService.set(prospect.data, prospect.prospectId);
          this.ngRedux.dispatch(savePlanIdForEnrollment(currentPlan.attributes.PLAN_ID));
          this.ngRedux.dispatch(savePlanNameForEnrollment(currentPlan.productName));

          const alertDuration = 1000; // ms

          this.snackBar.open(`Saving Information`, 'OK', {
            duration: alertDuration,
            panelClass: 'positive-panel-class'
          });

          setTimeout(() => {
            this.windowLocationService.goToUrl(enrollmentStartUrl);
            this.processingONX = false;
          }, alertDuration);

        }
      }

      return;
    };

    const isMetalLevelCatastrophic = currentPlan.attributes.METAL_LEVEL.toUpperCase() === 'CATASTROPHIC';
    const isMetalLevelSilver = currentPlan.attributes.METAL_LEVEL.toUpperCase() === 'SILVER';
    const matchedProspect = (() => {
      const onExchangeEligibilityState: OnExchangeEligibilityStateStructure
        = this.reducerUtilityService.getComponentStates()[ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME];
      const currentProspectId = onExchangeEligibilityState.currentProspectId;
      const matchedProspects = onExchangeEligibilityState.prospects.filter(p => p.prospectId === currentProspectId);
      if (matchedProspects.length > 0) {
        return matchedProspects[0];
      }
    })();

    const isAPTCEligible = ((): boolean => {
      if (matchedProspect) {
        return matchedProspect.data.APTC;
      }
      return false;
    })();

    const selectedAptcValue = ((): number => {
      if (matchedProspect) {
        return this.eligibilityManagerService.getSelectedMonthlyAptcForProspect(matchedProspect.prospectId);
      }
      return 0.00;
    })();

    let modalAlert: MatDialogRef<NoEnrollCatastrophicModalComponent | WarnEnrollNonSilverComponent, any>;
    let modalCloseSubscription: Subscription;

    if (isMetalLevelCatastrophic) {
      /** (HARD-STOP) Attempting to enroll in catastrophic - show phone number */
      modalAlert = this.matDialog.open(NoEnrollCatastrophicModalComponent, {
        maxWidth: '650px',
        maxHeight: '100vh',
        data: {
          region: currentPlan.attributes.PLAN_REGION[0]
        }
      });
    } else {
      if (!isMetalLevelSilver) {
        if (
          isAPTCEligible &&
          (!selectedAptcValue || selectedAptcValue === 0) &&
          (
            matchedProspect.data.CSRLevel === '04' ||
            matchedProspect.data.CSRLevel === '05' ||
            matchedProspect.data.CSRLevel === '06'
          )
        ) {
          /** (WARNING) Attempting to enroll in a NON-SILVER plan while having the ability to get tax credit AND cost-saving opportunity */
          modalAlert = this.matDialog.open(WarnEnrollNonSilverComponent, {
            maxWidth: '650px',
            maxHeight: '100vh',
            data: {
              showCSRWarning: true,
              showAPTCWarning: true,
              enrollStartUrl: enrollmentStartUrl,
              plan: currentPlan
            }
          });
          modalCloseSubscription = modalAlert.afterClosed().subscribe(canProceed => {
            if (canProceed) {
              proceedToEnroll();
              // Supposedly this auto-unsubcribes, but, better safe than sorry
              if (modalCloseSubscription) { modalCloseSubscription.unsubscribe(); }
            }
          });
        } else if (
          matchedProspect.data.CSRLevel === '04' ||
          matchedProspect.data.CSRLevel === '05' ||
          matchedProspect.data.CSRLevel === '06'
        ) {
          /** (WARNING) Attempting to enroll in a NON-SILVER plan while having the ability to get cost-saving opportunity */
          modalAlert = this.matDialog.open(WarnEnrollNonSilverComponent, {
            maxWidth: '650px',
            maxHeight: '100vh',
            data: {
              showCSRWarning: true,
              showAPTCWarning: false,
              enrollStartUrl: enrollmentStartUrl,
              plan: currentPlan
            }
          });;
          modalCloseSubscription = modalAlert.afterClosed().subscribe(canProceed => {
            if (canProceed) {
              proceedToEnroll();
              // Supposedly this auto-unsubcribes, but, better safe than sorry
              if (modalCloseSubscription) { modalCloseSubscription.unsubscribe(); }
            }
          });
        } else {
          /** (GOOD TO ENROLL) */
          proceedToEnroll();
        }
      } else if (
        isAPTCEligible &&
        (!selectedAptcValue || selectedAptcValue === 0)
      ) {
        /** (WARNING) Attempting to enroll in a NON-SILVER plan while having the ability to get tax credit */
        modalAlert = this.matDialog.open(WarnEnrollNonSilverComponent, {
          maxWidth: '650px',
          maxHeight: '100vh',
          data: {
            showCSRWarning: false,
            showAPTCWarning: true,
            enrollStartUrl: enrollmentStartUrl,
            plan: currentPlan
          }
        });
        modalCloseSubscription = modalAlert.afterClosed().subscribe(canProceed => {
          if (canProceed) {
            proceedToEnroll();
            // Supposedly this auto-unsubcribes, but, better safe than sorry
            if (modalCloseSubscription) { modalCloseSubscription.unsubscribe(); }
          }
        })
      } else {
        proceedToEnroll();
      }
    }

  }
  };